import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Landmark, Send, Loader2, Copy, RefreshCw, XCircle, CheckCircle2,
  Sparkles, Activity, Mail, ShieldCheck, Clock, TrendingUp, Users2,
  Wallet as WalletIcon,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { SovereignExport } from "./vcb/SovereignExport";
import { SelectionDock } from "./vcb/SelectionDock";
import { ActivationReceipts } from "./vcb/ActivationReceipts";
import { PhaseLedger } from "./vcb/PhaseLedger";
import { PremiumBadge } from "./vcb/PremiumBadge";
import { ActivationModeSwitch } from "./vcb/ActivationModeSwitch";
import { VCBStageDesk } from "./vcb/ReleaseStages";
import { ActivationEmailDialog } from "./vcb/ActivationEmailDialog";
import { PhaseRailLinks } from "./vcb/PhaseRailLinks";
import { RailSelector } from "./payments/RailSelector";

const STATUS_TONE = {
  sent:     { color: "#D8B23E", label: "Live · Awaiting Payment" },
  paid:     { color: "#2FB999", label: "Inducted · Paid" },
  expired:  { color: "#8A8A82", label: "Expired" },
  revoked:  { color: "#E5484D", label: "Revoked" },
  created:  { color: "#D8B23E", label: "Draft" },
};

export function AdminVCB({ api }) {
  const [invites, setInvites] = useState([]);
  const [counts, setCounts] = useState({ total: 0, live: 0, paid: 0, expired: 0, revoked: 0 });
  const [pulses, setPulses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [module, setModule] = useState({ enabled: false, mode: "admin_only" });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [form, setForm] = useState({
    email: "", fee_amount: 25000, expires_in_days: 30, note: "", payment_rail: "razorpay",
  });
  const [receiptsKey, setReceiptsKey] = useState(0);
  const [emailPulse, setEmailPulse] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, p, a, m] = await Promise.all([
        api.get("/admin/vcb/invites"),
        api.get("/admin/vcb/pulses?limit=60"),
        api.get("/admin/vcb/analytics"),
        api.get("/vcb/module-status"),
      ]);
      setInvites(i.data?.invitations || []);
      setCounts(i.data?.counts || { total: 0, live: 0, paid: 0, expired: 0, revoked: 0 });
      setPulses(p.data?.pulses || []);
      setAnalytics(a.data || null);
      setModule(m.data || { enabled: false });
    } catch { toast.error("Could not load Vinci Core Banking desk."); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    if (!form.email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (form.fee_amount < 100)     { toast.error("Fee must be at least ₹100"); return; }
    setIssuing(true);
    try {
      const r = await api.post("/admin/vcb/invites", {
        email: form.email.trim(),
        fee_amount: Number(form.fee_amount),
        expires_in_days: Number(form.expires_in_days),
        note: form.note || "",
        payment_rail: form.payment_rail,
      });
      toast.success("Executive Banking Invitation dispatched", {
        description: `Fee: ₹${Number(form.fee_amount).toLocaleString("en-IN")} · valid ${form.expires_in_days} days`,
      });
      setShowInvite(false);
      setForm({ email: "", fee_amount: 25000, expires_in_days: 30, note: "", payment_rail: "razorpay" });
      // Auto-copy the induction link to clipboard for quick admin sharing
      const link = r.data?.induction_link;
      if (link && navigator.clipboard) {
        try { await navigator.clipboard.writeText(link); toast.info("Induction link copied"); } catch {}
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue invitation");
    } finally { setIssuing(false); }
  };

  const revoke = async (row) => {
    if (!confirm(`Revoke the invitation for ${row.email}?`)) return;
    try {
      await api.post(`/admin/vcb/invites/${row.id}/revoke`);
      toast.success("Invitation revoked");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  const resend = async (row) => {
    try {
      await api.post(`/admin/vcb/invites/${row.id}/resend`);
      toast.success(`Reminder dispatched to ${row.email}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  const copyLink = async (row) => {
    const origin = window.location.origin;
    const link = `${origin}/vcb/induct/${row.token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Induction link copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-vcb-desk">
      <ActivationModeSwitch api={api} module={module} onChange={load} />

      {/* Hero */}
      <div className="rounded-sm border border-[#D8B23E]/40 bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{ background: "radial-gradient(circle at 20% 0%, #D8B23E 0%, transparent 40%), radial-gradient(circle at 90% 100%, #2FB999 0%, transparent 45%)" }} />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
              Section VII · Vinci Core Banking
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#F5F5F0] mt-1">Executive Banking Desk</h2>
            <p className="font-body text-sm text-[#C9C9C2] mt-2 max-w-xl leading-relaxed">
              Issue paid induction invitations. Every accepted invitation unlocks
              wallet-to-wallet privileges, zero handling charges on intra-ecosystem
              trades, and permanent client mapping.
            </p>
          </div>
          <Button
            data-testid="vcb-issue-invite-btn"
            onClick={() => setShowInvite(true)}
            className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450]"
          >
            <Sparkles size={12} className="mr-1.5" /> Induct into Banking
          </Button>
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          <StatTile label="Total" value={counts.total} />
          <StatTile label="Live"    value={counts.live}    tone="#D8B23E" />
          <StatTile label="Inducted" value={counts.paid}   tone="#2FB999" />
          <StatTile label="Expired" value={counts.expired} tone="#8A8A82" />
          <StatTile label="Revoked" value={counts.revoked} tone="#E5484D" />
        </div>
      </div>

      {/* Analytics Slice */}
      {analytics && <VCBAnalyticsSection analytics={analytics} api={api} />}

      {/* Precision Activation · Member Registry */}
      <PrecisionActivation api={api} onChange={async () => { setReceiptsKey((k) => k + 1); await load(); }} />

      {/* Stage-wise Feature Release · customer profile + report */}
      <VCBStageDesk api={api} />

      {/* Activation Receipts · welcome-email ledger */}
      <ActivationReceipts api={api} refreshKey={receiptsKey} />

      {/* PREMIUM two-phase onboarding ledger */}
      <PhaseLedger api={api} invitations={invites} onChange={load} />

      {/* Invitations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-[#F5F5F0]">Executive Invitations</h3>
          <Button variant="outline" size="sm" onClick={load}
                    className="border-white/20 text-[#C9C9C2] text-[10px] font-grotesk uppercase tracking-[0.22em]">
            <RefreshCw size={11} className="mr-1" /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="text-[#8A8A82] font-body">Loading invitations…</div>
        ) : invites.length === 0 ? (
          <div className="rounded-sm border border-dashed border-[#D8B23E]/30 p-8 text-center">
            <Landmark size={22} className="mx-auto text-[#D8B23E] opacity-60 mb-2" />
            <div className="font-serif text-lg text-[#F5F5F0]">No invitations issued yet</div>
            <div className="text-[11px] text-[#8A8A82] font-body mt-1 italic">
              Click "Induct into Banking" above to send your first executive invitation.
            </div>
          </div>
        ) : (
          <div className="rounded-sm border border-white/10 overflow-x-auto bg-[#0A0906]">
            <table className="w-full text-sm" data-testid="vcb-invites-table">
              <thead className="border-b border-white/10 bg-[#12100A]">
                <tr className="text-left font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
                  <th className="py-3 px-3">Recipient</th>
                  <th className="py-3 px-3 text-right">Fee</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">Expiry</th>
                  <th className="py-3 px-3">Issued By</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((r) => {
                  const tone = STATUS_TONE[r.status] || STATUS_TONE.sent;
                  return (
                    <tr key={r.id} data-testid={`vcb-invite-row-${r.id}`}
                         className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="py-3 px-3">
                        <div className="font-body text-sm text-[#F5F5F0]">{r.email}</div>
                        {r.note && (
                          <div className="text-[10px] text-[#8A8A82] font-body italic mt-0.5 max-w-md truncate">
                            {r.note}
                          </div>
                        )}
                        {r.status !== "revoked" && <PhaseRailLinks api={api} invite={r} onChange={load} />}
                      </td>
                      <td className="py-3 px-3 text-right font-body text-[#D8B23E]">
                        ₹{Number(r.fee_amount).toLocaleString("en-IN")}
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]"
                                style={{ color: tone.color }}>
                          {r.status === "paid" && <CheckCircle2 size={11} />}
                          {r.status === "sent" && <Clock size={11} />}
                          {r.status === "revoked" && <XCircle size={11} />}
                          {tone.label}
                        </span>
                        {r.status === "paid" && r.consumed_by && (
                          <div className="text-[10px] text-[#8A8A82] font-body italic mt-0.5">
                            Paid by {r.consumed_by}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-[11px] font-mono text-[#C9C9C2]">
                        {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3 px-3 text-[11px] font-body text-[#8A8A82]">
                        {r.created_by || "—"}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button title="Copy induction link"
                                   data-testid={`vcb-copy-${r.id}`}
                                   onClick={() => copyLink(r)}
                                   className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                            <Copy size={12} />
                          </button>
                          {(r.status === "sent" || r.status === "created") && (
                            <>
                              <button title="Resend invitation email"
                                       data-testid={`vcb-resend-${r.id}`}
                                       onClick={() => resend(r)}
                                       className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                                <Mail size={12} />
                              </button>
                              <button title="Revoke invitation"
                                       data-testid={`vcb-revoke-${r.id}`}
                                       onClick={() => revoke(r)}
                                       className="p-1.5 text-[#8A8A82] hover:text-[#E5484D]">
                                <XCircle size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pulses */}
      <div className="space-y-3">
        <h3 className="font-serif text-xl text-[#F5F5F0]">Banking Pulse · Live Audit</h3>
        {pulses.length === 0 ? (
          <div className="text-[11px] text-[#8A8A82] font-body italic">No banking events yet.</div>
        ) : (
          <div className="rounded-sm border border-white/10 bg-[#0A0906] max-h-[360px] overflow-y-auto"
                data-testid="vcb-pulses">
            {pulses.map((p) => (
              <div key={p.id || p._id}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0">
                <Activity size={11} className="text-[#D8B23E] shrink-0" />
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#2FB999] shrink-0 w-40">
                  {p.event.replace(/_/g, " ")}
                </div>
                <div className="text-[11px] font-body text-[#C9C9C2] truncate flex-1">
                  {p.meta?.email || p.meta?.order_id || p.meta?.payment_id || p.user_id || "—"}
                  {p.meta?.receipt_no && <span className="text-[#D8B23E] font-mono"> · {p.meta.receipt_no}</span>}
                  {p.meta?.status && ` · ${p.meta.status}`}
                  {p.meta?.fee && ` · ₹${Number(p.meta.fee).toLocaleString("en-IN")}`}
                  {p.meta?.amount && ` · ₹${Number(p.meta.amount).toLocaleString("en-IN")}`}
                  {p.meta?.to_mode && ` · ${(p.meta.from_mode || "").replace("_", " ")} → ${p.meta.to_mode.replace("_", " ")}`}
                  {p.meta?.by && ` · ${p.meta.by}`}
                </div>
                {p.event === "module_mode_changed" && (p.meta?.selected || []).length > 0 && (
                  <button data-testid={`vcb-pulse-email-${p.id || p._id}`}
                          onClick={() => setEmailPulse(p)}
                          title="Preview / resend activation email"
                          className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#D8B23E] hover:text-[#E5C450] shrink-0">
                    <Mail size={11} /> Email
                  </button>
                )}
                <div className="text-[10px] font-mono text-[#8A8A82] shrink-0">
                  {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ActivationEmailDialog api={api} pulse={emailPulse} open={!!emailPulse} onClose={() => setEmailPulse(null)} />

      {/* Issue Invitation Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="vcb-invite-dialog"
                        className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <Sparkles size={16} className="text-[#D8B23E]" /> Executive Banking Invitation
            </DialogTitle>
            <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
              One-time paid induction into Vinci Core Banking. The recipient
              receives a signed invitation email with a private payment link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Recipient Email</Label>
              <Input data-testid="vcb-invite-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="director@buyerfirm.com"
                      className="bg-black/50 border-white/20 text-[#F5F5F0]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Mapping Fee (₹)</Label>
                <Input type="number" data-testid="vcb-invite-fee"
                        value={form.fee_amount} min={100} step={500}
                        onChange={(e) => setForm({ ...form, fee_amount: e.target.value })}
                        className="bg-black/50 border-white/20 text-[#D8B23E] font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Expiry (days)</Label>
                <Input type="number" data-testid="vcb-invite-expiry"
                        value={form.expires_in_days} min={1} max={180}
                        onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                        className="bg-black/50 border-white/20 text-[#F5F5F0] font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Personal Note (optional)</Label>
              <Textarea data-testid="vcb-invite-note"
                         value={form.note}
                         onChange={(e) => setForm({ ...form, note: e.target.value })}
                         rows={3}
                         placeholder="Included inside the invitation email as a highlighted note."
                         className="bg-black/50 border-white/20 text-[#F5F5F0]" />
            </div>
            <RailSelector testId="vcb-invite-rail" value={form.payment_rail} onChange={(r) => setForm({ ...form, payment_rail: r })}
                          label="Activation Fee Rail · Phase 1 & 2 links generated on both rails, this one is dispatched" />
            <div className="text-[10px] text-[#8A8A82] font-body italic border-t border-white/5 pt-3 flex items-center gap-2">
              <ShieldCheck size={11} className="text-[#2FB999]" />
              Razorpay and PhonePe links are both minted. The induction link is auto-copied to your clipboard on issue.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowInvite(false)}
                     className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.24em] text-[10px]">
              Cancel
            </Button>
            <Button data-testid="vcb-invite-submit"
                     onClick={issue} disabled={issuing}
                     className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
              {issuing
                ? <Loader2 size={11} className="animate-spin" />
                : <><Send size={11} className="mr-1" /> Issue & Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, tone }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                 className="border border-white/10 rounded-sm bg-black/40 px-4 py-3">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{label}</div>
      <div className="font-serif text-2xl mt-0.5"
            style={{ color: tone || "#F5F5F0" }}>{value}</div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Precision Activation · Member Registry
// ═══════════════════════════════════════════════════════════════════
function PrecisionActivation({ api, onChange }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [readyOnly, setReadyOnly] = useState(true);
  const [pulseId, setPulseId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [selBusy, setSelBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/vcb/candidates?ready_only=${readyOnly}`);
      const next = r.data?.rows || [];
      setRows(next);
      setCounts(r.data?.counts || {});
      const live = new Set(next.filter((x) => x.ready_for_activation && x.vcb_status !== "inducted").map((x) => x.id));
      setSelected((prev) => new Set([...prev].filter((id) => live.has(id))));
    } catch { toast.error("Could not load member registry"); }
    finally { setLoading(false); }
  }, [api, readyOnly]);

  useEffect(() => { load(); }, [load]);

  const selectableRows = rows.filter((r) => r.ready_for_activation && r.vcb_status !== "inducted");
  const selectedRows = selectableRows.filter((r) => selected.has(r.id));
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableRows.map((r) => r.id)));

  const activateSelected = async () => {
    if (selectedRows.length === 0) return;
    setSelBusy(true);
    try {
      const r = await api.post("/admin/vcb/activate-bulk",
                               { scope: "selected",
                                 emails: selectedRows.map((x) => x.email),
                                 note: "Precision Activation · selected rows" });
      toast.success("Precision Pulse fired", {
        description: `${r.data.activated} activated · ${r.data.skipped} skipped · ${r.data.receipts_sent ?? 0} welcome receipt${r.data.receipts_sent === 1 ? "" : "s"} emailed`,
      });
      const doneEmails = new Set((r.data.targets || []).filter((t) => t.status !== "error").map((t) => t.email));
      setRows((prev) => readyOnly
        ? prev.filter((x) => !doneEmails.has(x.email))
        : prev.map((x) => doneEmails.has(x.email) ? { ...x, vcb_status: "inducted", vcb_fee_waived: true } : x));
      setCounts((c) => ({ ...c,
        ready: Math.max(0, (c.ready ?? 0) - r.data.activated),
        inducted: (c.inducted ?? 0) + r.data.activated }));
      setSelected(new Set());
      await load(); await onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Selected activation failed"); }
    finally { setSelBusy(false); }
  };

  const activateOne = async (row) => {
    setBusy(row.id);
    try {
      const r = await api.post("/admin/vcb/activate-without-payment",
                        { email: row.email,
                           note: "Precision Activation · individual" });
      toast.success(`${row.email} activated`, {
        description: r.data?.receipt_no
          ? `Receipt ${r.data.receipt_no} · ${r.data.receipt_email === "dispatched" ? "welcome email sent" : "email " + r.data.receipt_email}`
          : "Fee waived · lifetime privilege",
      });
      setPulseId(row.id);
      setTimeout(() => setPulseId(null), 1600);
      await load(); await onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally { setBusy(null); }
  };

  const activateAllReady = async () => {
    const ready = rows.filter((r) => r.ready_for_activation
                                        && r.vcb_status !== "inducted");
    if (ready.length === 0) {
      toast.info("No candidates ready for activation right now");
      return;
    }
    if (!confirm(`Activate all ${ready.length} customer(s) who have completed B-KYC + e-signature?\n\nEach will be inducted fee-waived and will see VCB in their dashboard immediately.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/vcb/activate-bulk",
                                  { scope: "all_ready",
                                     note: "Precision Activation · Vinci Pulse Master" });
      toast.success(`Vinci Pulse fired`, {
        description: `${r.data.activated} activated · ${r.data.skipped} skipped · ${r.data.receipts_sent ?? 0} welcome receipts emailed`,
      });
      await load(); await onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk activation failed"); }
    finally { setBulkBusy(false); }
  };

  const readyCount = rows.filter((r) => r.ready_for_activation
                                             && r.vcb_status !== "inducted").length;

  return (
    <div className="space-y-4" data-testid="vcb-precision-activation">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-xl text-[#F5F5F0] inline-flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#D8B23E]" /> Precision Activation · Member Registry
        </h3>
        <div className="inline-flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="vcb-ready-only-filter"
                    checked={readyOnly}
                    onChange={(e) => setReadyOnly(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#D8B23E]" />
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2]">
              Ready for Activation Only
            </span>
          </label>
        </div>
      </div>

      {/* Vinci Pulse master switch */}
      <div className="rounded-sm border border-[#D8B23E]/50 bg-gradient-to-br from-[#12100A] via-[#0A0906] to-[#12100A] p-5 md:p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
            Vinci Pulse · Master Activation
          </div>
          <div className="font-serif text-2xl text-[#F5F5F0] mt-0.5">
            {readyCount} customer{readyCount === 1 ? "" : "s"} ready to enter Sync
          </div>
          <div className="text-[11px] text-[#8A8A82] italic mt-1 font-body">
            Every candidate below has completed B-KYC + e-signature. Firing this
            pulse inducts them all in one atomic sweep — each with the fee waived.
            Or tick individual rows below to activate a hand-picked subset.
          </div>
        </div>
        <Button data-testid="vcb-master-activate-all"
                 onClick={activateAllReady}
                 disabled={bulkBusy || readyCount === 0}
                 className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[11px] hover:bg-[#E5C450] px-6 py-5">
          {bulkBusy
            ? <Loader2 size={13} className="animate-spin" />
            : <><Sparkles size={12} className="mr-1.5" /> Activate All Ready</>}
        </Button>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Ready to Activate" value={counts.ready ?? 0} tone="#D8B23E" />
        <StatTile label="Already Inducted" value={counts.inducted ?? 0} tone="#2FB999" />
        <StatTile label="Pending KYC" value={counts.pending_kyc ?? 0} tone="#E5A93E" />
        <StatTile label="Pending e-Sign" value={counts.pending_esign ?? 0} tone="#8A8A82" />
      </div>

      {/* Registry table */}
      {loading ? (
        <div className="text-[#8A8A82] font-body py-6 text-center">
          <Loader2 size={16} className="animate-spin mx-auto" />
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-sm py-8 text-center">
          <ShieldCheck size={20} className="mx-auto text-[#8A8A82] opacity-40 mb-2" />
          <div className="font-serif text-lg text-[#F5F5F0]">Registry empty</div>
          <div className="text-[11px] text-[#8A8A82] italic mt-1">
            {readyOnly ? "Toggle 'Ready Only' off to see every customer." : "No customers on the platform yet."}
          </div>
        </div>
      ) : (
        <div className="rounded-sm border border-white/10 bg-[#0A0906] overflow-x-auto"
              data-testid="vcb-registry-table">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#12100A]">
              <tr className="text-left font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
                <th className="py-3 pl-4 pr-1 w-8">
                  <input type="checkbox" data-testid="vcb-select-all"
                         checked={allSelected}
                         ref={(el) => { if (el) el.indeterminate = !allSelected && selectedRows.length > 0; }}
                         disabled={selectableRows.length === 0}
                         onChange={toggleAll}
                         title="Select every member ready for activation"
                         className="w-3.5 h-3.5 accent-[#D8B23E] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30" />
                </th>
                <th className="py-3 px-3">Member</th>
                <th className="py-3 px-3">Role</th>
                <th className="py-3 px-3">B-KYC</th>
                <th className="py-3 px-3">e-Sign</th>
                <th className="py-3 px-3">VCB Status</th>
                <th className="py-3 px-3 text-right">Precision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const inducted = r.vcb_status === "inducted";
                const isReady = r.ready_for_activation && !inducted;
                const pulsing = pulseId === r.id;
                const checked = selected.has(r.id);
                return (
                  <motion.tr key={r.id}
                              animate={pulsing ? {
                                boxShadow: [
                                  "inset 0 0 0px 0 rgba(216,178,62,0)",
                                  "inset 0 0 24px 3px rgba(216,178,62,0.65)",
                                  "inset 0 0 0px 0 rgba(216,178,62,0)",
                                ],
                                backgroundColor: [
                                  "rgba(216,178,62,0)",
                                  "rgba(216,178,62,0.14)",
                                  "rgba(216,178,62,0)",
                                ],
                              } : { backgroundColor: checked ? "rgba(216,178,62,0.07)" : "rgba(216,178,62,0)" }}
                              transition={{ duration: pulsing ? 1.4 : 0.25 }}
                              style={{ boxShadow: checked ? "inset 2px 0 0 0 #D8B23E" : undefined }}
                              className="border-t border-white/5 hover:bg-white/[0.02]"
                              data-testid={`vcb-registry-row-${r.id}`}
                              data-selected={checked ? "true" : "false"}>
                    <td className="py-3 pl-4 pr-1">
                      <input type="checkbox"
                             data-testid={`vcb-select-${r.id}`}
                             checked={checked}
                             disabled={!isReady}
                             onChange={() => toggleOne(r.id)}
                             title={isReady ? "Select for activation" : inducted ? "Already inducted" : "Not yet ready (KYC / e-sign pending)"}
                             className="w-3.5 h-3.5 accent-[#D8B23E] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-body text-sm text-[#F5F5F0]">{r.email}</div>
                      {r.name && (
                        <div className="text-[10px] text-[#8A8A82] font-body italic">{r.name}</div>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2]">
                        {r.role}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {r.kyc_verified ? (
                        <span className="inline-flex items-center gap-1 text-[#2FB999] font-grotesk text-[9px] uppercase tracking-[0.22em]">
                          <CheckCircle2 size={11} /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#E5A93E] font-grotesk text-[9px] uppercase tracking-[0.22em]">
                          <Clock size={11} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      {r.esign_completed ? (
                        <span className="inline-flex items-center gap-1 text-[#2FB999] font-grotesk text-[9px] uppercase tracking-[0.22em]">
                          <CheckCircle2 size={11} /> Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#8A8A82] font-grotesk text-[9px] uppercase tracking-[0.22em]">
                          <Clock size={11} /> Awaiting
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      {inducted ? (
                        <span className="inline-flex items-center gap-1 text-[#2FB999] font-grotesk text-[9px] uppercase tracking-[0.22em]">
                          <CheckCircle2 size={11} /> {r.vcb_fee_waived ? "Fee-waived" : "Inducted"}
                        </span>
                      ) : (
                        <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
                          {r.vcb_status.replace("_", " ")}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right">
                      {inducted ? (
                        <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#2FB999]">
                          Active
                        </span>
                      ) : (
                        <Button size="sm"
                                 data-testid={`vcb-activate-individual-${r.id}`}
                                 onClick={() => activateOne(r)}
                                 disabled={busy === r.id || !isReady}
                                 className={`font-grotesk uppercase tracking-[0.24em] text-[9px] ${
                                   isReady
                                     ? "bg-[#D8B23E] text-black hover:bg-[#E5C450] shadow-[0_0_16px_-4px_rgba(216,178,62,0.6)]"
                                     : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                                 }`}>
                          {busy === r.id
                            ? <Loader2 size={10} className="animate-spin" />
                            : <><Sparkles size={10} className="mr-1" /> Activate</>}
                        </Button>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <SelectionDock rows={selectedRows} busy={selBusy}
                     onClear={() => setSelected(new Set())}
                     onActivate={activateSelected} />
    </div>
  );
}

function StatTileHidden() { return null; /* stub retained for future */ }
const RAIL_TONE = {
  wallet: "#D8B23E", upi: "#2FB999", imps: "#5EEAD4",
  neft:  "#B87333", rtgs: "#E5A93E",
};
const RAIL_LABEL = { wallet: "Wallet", upi: "UPI", imps: "IMPS", neft: "NEFT", rtgs: "RTGS" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN",
                        { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function VCBAnalyticsSection({ analytics, api }) {
  const [scope, setScope] = useState("last_7d");
  const rails = analytics?.rails?.[scope] || {};
  const totalVolume = Object.values(rails).reduce((s, r) => s + (r?.volume || 0), 0);
  return (
    <div className="space-y-4" data-testid="vcb-analytics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-xl text-[#F5F5F0] inline-flex items-center gap-2">
          <TrendingUp size={16} className="text-[#D8B23E]" /> Sovereign Data Slice
        </h3>
        <div className="inline-flex items-center gap-2">
        <div className="inline-flex border border-white/10 rounded-sm">
          {["last_24h", "last_7d", "all_time"].map((k) => (
            <button key={k}
                     data-testid={`vcb-scope-${k}`}
                     onClick={() => setScope(k)}
                     className={`px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] ${
                       scope === k
                         ? "bg-[#D8B23E] text-black"
                         : "text-[#C9C9C2] hover:bg-white/5"
                     }`}>
              {k.replace("_", " ").replace("last", "").trim()}
            </button>
          ))}
        </div>
        <SovereignExport api={api} scope={scope} />
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Inducted Users" value={analytics?.users?.inducted_total || 0} tone="#2FB999" />
        <StatTile label="Unique Mapped Pairs" value={analytics?.mappings?.unique_pairs || 0} tone="#D8B23E" />
        <StatTile label="Live Mappings" value={analytics?.mappings?.accepted || 0} />
        <StatTile label="Suspended" value={analytics?.users?.suspended || 0}
                    tone={(analytics?.users?.suspended || 0) > 0 ? "#E5484D" : "#8A8A82"} />
      </div>

      {/* Rail slice — the industry-transaction dataset */}
      <div className="border border-[#D8B23E]/30 rounded-sm bg-[#0A0906] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#D8B23E]">
            Rail Split · {scope.replace("_", " ")}
          </div>
          <div className="font-body text-sm text-[#C9C9C2]">
            Total volume · <span className="text-[#D8B23E] font-mono">{inr(totalVolume)}</span>
          </div>
        </div>
        {totalVolume === 0 ? (
          <div className="text-[11px] text-[#8A8A82] italic font-body py-4 text-center">
            No transfers in this window yet.
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(rails).map(([rail, r]) => {
              const pct = totalVolume > 0 ? (r.volume / totalVolume) * 100 : 0;
              return (
                <div key={rail} data-testid={`vcb-rail-${rail}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full"
                             style={{ background: RAIL_TONE[rail] }} />
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]"
                             style={{ color: RAIL_TONE[rail] }}>{RAIL_LABEL[rail]}</span>
                      <span className="text-[10px] text-[#8A8A82] font-mono">
                        {r.count} {r.count === 1 ? "tx" : "txs"}
                      </span>
                    </div>
                    <div className="text-[11px] font-mono text-[#F5F5F0]">
                      {inr(r.volume)} · <span className="text-[#8A8A82]">{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-sm overflow-hidden">
                    <div className="h-full" style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${RAIL_TONE[rail]}, ${RAIL_TONE[rail]}88)`,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top payers · 30d */}
      {(analytics?.top_payers_30d || []).length > 0 && (
        <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#D8B23E] mb-3 inline-flex items-center gap-1.5">
            <Users2 size={11} /> Top Payers · 30 days
          </div>
          <div className="space-y-1.5">
            {analytics.top_payers_30d.map((p, i) => (
              <div key={p.email}
                    className="flex items-center justify-between text-[12px] font-body text-[#C9C9C2] py-1.5 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] text-[#8A8A82] w-6">#{i + 1}</span>
                  <span className="truncate">{p.email}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[10px] font-mono text-[#8A8A82]">{p.count} tx</span>
                  <span className="font-mono text-[#D8B23E]">{inr(p.volume)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
